import { CanActivateFn, Data, Router } from '@angular/router';
import { inject } from '@angular/core';
import { PrivilegeService } from '@core/services/application/privilege/privilege.service';
import { flattenDeep } from 'lodash';
import { map } from 'rxjs/operators';
import { PageAccessPermission, PrivilegeKey } from '@core/models';
import { SystemSetting } from '@core/services/settings/system-settings.service';
import { AccessDeniedPath } from '@core/constants';

/**
 * Interface for route privilege requirements
 */
export interface PrivilegeData {
  permission?: PageAccessPermission[];
  systemSettings?: (keyof SystemSetting)[];
}

/**
 * In an effort to strongly type our top-level routes, we have included GuestData to enforce guestTypes be added to new routes.
 * If it is a Nymbl user, they will not have a guestType, so undefined is passed, instead.
 */
export interface GuestData {
  guestType?: string;
}
export interface RouteData extends Data {
  data?: PrivilegeData;
}
export type RoutesData = RouteData[];

/**
 * Guard that controls route access based on user privileges
 *
 * Features:
 * - Validates user privileges against required route permissions
 * - Supports nested route permission inheritance
 * - Filters out report_view permissions (handled separately)
 *
 * Usage:
 * ```typescript
 * {
 *   path: 'protected-route',
 *   component: ProtectedComponent,
 *   data: {
 *     permission: ['required_privilege']
 *   },
 *   canActivate: [privilegeGuard]
 * }
 * ```
 * @link https://www.beech.it/en/blog/angular-routes-authentication-based-on-roles-and-permissions
 * @link https://medium.com/echohub/angular-role-based-routing-access-with-angular-guard-dbecaf6cd685
 * @param route Current route being activated
 * @param state Current router state
 * @returns Observable<boolean | UrlTree> - true if access allowed, or redirect to access denied
 */
export const privilegeGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const privilegeService = inject(PrivilegeService);
  const hasChildren = route.children?.length > 0;

  let permissions: PrivilegeKey[];
  permissions = flattenDeep(
    route.pathFromRoot
      .filter((path) => path.data['permission'])
      .map((p) => p.data['permission'])
      .filter((p) => !p.includes('report_view'))
  );

  // todo rename this file to 'reportPrivileges' & move to reports module(?)
  // console.log(permissions);
  return (
    !permissions ||
    hasChildren ||
    privilegeService.hasPrivileges(permissions).pipe(
      map((p) => {
        return p || router.parseUrl(AccessDeniedPath);
      })
    )
  );
};
